import {
  AdditionalViewField,
  FormConfigField,
  UseCaseConfig,
  WorkspaceTask,
} from "../shared/types";

// ----------------------------  Raw value helpers  ----------------------------
// A field names the sample columns it shows (source_columns), picked from the
// backend's list: an RDS column (predicted_verdict), read from the sample row
// (task.raw_values), or a path into the objects / objects_override JSON
// (objects.predictedVerdict, objects.complianceFix.evidences), read per
// object on object presence records.

export type SourceValues = Record<string, unknown>;

const isRecord = (v: unknown): v is SourceValues =>
  !!v && typeof v === "object" && !Array.isArray(v);

const toText = (v: unknown): string | undefined => {
  if (v === null || v === undefined) return undefined;
  if (Array.isArray(v)) {
    const parts = v.map(toText).filter((x): x is string => x !== undefined);
    return parts.length ? parts.join(", ") : undefined;
  }
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v).trim();
  return s ? s : undefined;
};

const columnsOf = (f: { source_columns?: string[] }): string[] =>
  (f.source_columns ?? []).map((c) => c.trim()).filter(Boolean);

// ----------------------------  Public API  ----------------------------

// Follow a dotted path ("objects.complianceFix.comment") through nested objects.
export const valueAt = (source: SourceValues, path: string): unknown =>
  path.split(".").reduce<unknown>((node, key) => (isRecord(node) ? node[key] : undefined), source);

/** Value of the first of the field's columns that has one, or undefined. */
export const fieldValue = (
  f: { source_columns?: string[] },
  source: SourceValues,
): string | undefined => {
  for (const col of columnsOf(f)) {
    const v = toText(valueAt(source, col));
    if (v !== undefined) return v;
  }
  return undefined;
};

/** Shown value per field label (fields without a value are left out). */
export const fieldValues = (
  fields: FormConfigField[],
  source: SourceValues,
): Record<string, string> => {
  const out: Record<string, string> = {};
  fields.forEach((f) => {
    const v = fieldValue(f, source);
    if (v !== undefined) out[f.label] = v;
  });
  return out;
};

/** Fields shown for one record or object: those with a value (others are hidden). */
export const visibleFields = (fields: FormConfigField[], source: SourceValues): FormConfigField[] =>
  fields.filter((f) => fieldValue(f, source) !== undefined);

/** The record-level values: the sample row. */
export const recordSource = (task: WorkspaceTask): SourceValues => task.raw_values || {};

// ----------------------------  Object presence  ----------------------------
// An object presence record (is_object_presence) carries one entry per object in
// objects (objectName, predictedVerdict, predictedReasoning, isHumanOverride,
// complianceFix) and the inspector's overrides in objects_override (overrideVerdict,
// overrideReason, overriddenEvidences; only for the objects overridden), matched by
// objectName. An object's source holds
// just its two entries, so record columns never show on an object.

export interface ObjectCheck {
  objectName: string;
  source: { objects: SourceValues; objects_override: SourceValues };
}

export const objectChecks = (task: WorkspaceTask): ObjectCheck[] => {
  const raw = recordSource(task);
  if (raw.is_object_presence !== true || !Array.isArray(raw.objects)) return [];
  const overrides = Array.isArray(raw.objects_override)
    ? raw.objects_override.filter(isRecord)
    : [];
  return raw.objects
    .filter(isRecord)
    .map((o) => {
      const objectName = toText(o.objectName) ?? "";
      const override = overrides.find((x) => toText(x.objectName) === objectName);
      return { objectName, source: { objects: o, objects_override: override ?? {} } };
    })
    .filter((o) => !!o.objectName);
};

// ----------------------------  Review scope  ----------------------------
// What one record or object asks of the auditor; mirrors the backend's
// audit/review_scope.py, which holds a submit to the same rules.

/** One record, or one object of an object presence record, as reviewed. */
export interface ReviewUnit {
  // undefined for the record itself
  objectName?: string;
  source: SourceValues;
}

/** The record, or its objects on an object presence record. */
export const reviewUnits = (task: WorkspaceTask): ReviewUnit[] => {
  const objects = objectChecks(task);
  return objects.length
    ? objects.map((o) => ({ objectName: o.objectName, source: o.source }))
    : [{ source: recordSource(task) }];
};

/** In the additional view (read-only rows, no field rows): one of its flag columns is true. */
export const showsAdditionalView = (config: UseCaseConfig, unit: ReviewUnit): boolean =>
  (config.additional_view_enable_flags ?? []).some((c) => valueAt(unit.source, c) === true);

/** Fields reviewed on the unit: none in the additional view, else those with a value. */
export const unitFields = (
  config: UseCaseConfig,
  fields: FormConfigField[],
  unit: ReviewUnit,
): FormConfigField[] =>
  showsAdditionalView(config, unit) ? [] : visibleFields(fields, unit.source);

/** The checklist shows when it has items: where a flag column is true, or everywhere if none. */
export const showsChecklist = (config: UseCaseConfig, unit: ReviewUnit): boolean => {
  if (!config.checklist_items.length) return false;
  const flags = config.checklist_enable_flags ?? [];
  return !flags.length || flags.some((c) => valueAt(unit.source, c) === true);
};

// ----------------------------  Details  ----------------------------

export interface DetailRow {
  label: string;
  value: string;
}

/**
 * Every configured additional-view row, in display order. A row is kept even when its
 * columns are missing or null; its value is then "" (shown as a dash).
 */
export const detailRows = (
  details: AdditionalViewField[] | undefined,
  unit: ReviewUnit,
): DetailRow[] =>
  [...(details ?? [])]
    .sort((a, b) => a.display_order - b.display_order)
    .map((d) => ({ label: d.label, value: fieldValue(d, unit.source) ?? "" }));
